import cn from "classnames";
import { reportColumnAggregateTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import mean from "lodash/mean";
import sum from "lodash/sum";
import React, { Component } from "react";
import CommonReport from "./commonReport";

class DriverAppUsageReport extends CommonReport {
    extractColumns = () => {
        const { columns } = this.props.data || {};
        return columns.map((column) => {
            if (column.propertyName === "CarLogistId") {
                column.label = "Логист";
            }
            return {
                ...this.cellProps(column),
                filterable: !["CarLogistId", "DriverId"].includes(column.propertyName),
                aggregate: (leafValues) => {
                    if (!this.numericClrTypes.includes(column.clrType)) {
                        return null;
                    }
                    switch (column.aggregateType) {
                        case reportColumnAggregateTypesEnum.Average:
                            return Math.round(mean(leafValues));
                        case reportColumnAggregateTypesEnum.Sum:
                            return sum(leafValues);
                        default:
                            return null;
                    }
                },
                Aggregated: ({ value, row }) => {
                    switch (column.propertyName) {
                        case "AveragePercent": {
                            const { averagePercent } = row.values;
                            return <strong>{formatValue(averagePercent / 100, "percent")}</strong>;
                        }
                        default:
                            return value !== null ? <strong>{formatValue(value, "double")}</strong> : "";
                    }
                },
            };
        });
    };

    cellProps = (column) => {
        return {
            Header: column.label,
            accessor: columnName(column),
            Cell: (props) => {
                const { carLogistId, driverId } = props.row.values;
                const value = carLogistId + driverId;
                const uniqueId = { [`data-${props.column.id}`.toLowerCase()]: value };
                switch (props.column.id) {
                    case "driverDisplayName": {
                        const onClick = () => {
                            props.modal({
                                name: "infoModal",
                                windowMod: "wide",
                                title: props.row.values.driverDisplayName,
                                body: props.renderRowSubComponent(props.row),
                            });
                        };
                        return (
                            <div className="cursor-pointer" onClick={onClick}>
                                <i
                                    className={cn("r-item__expander", {
                                        "icon-uEAAE-angle-right-solid": !props.row.isExpanded,
                                        "icon-uEA61-angle-down": props.row.isExpanded,
                                    })}
                                />
                                <span {...uniqueId}>{props.value}</span>
                            </div>
                        );
                    }
                    case "carLogistId": {
                        const { carLogistDisplayName } = props.row.subRows[0].values;
                        return carLogistDisplayName || "Без логиста";
                    }
                    case "averagePercent": {
                        return <span {...uniqueId}>{formatValue(props.value / 100, "percent")}</span>;
                    }
                    default: {
                        return <span {...uniqueId}>{formatValue(props.value, column.clrType)}</span>;
                    }
                }
            },
            Footer: () => this.footerVal(column),
            ...typeSpecificProps(column),
        };
    };

    tableExtraProps = () => {
        const { columns } = this.props.data || {};
        const hiddenColumns = columns
            .filter(
                (column) =>
                    column.isAdditionalColumn ||
                    column.isFooterData ||
                    ["CarLogistDisplayName", "DriverId"].includes(column.propertyName) ||
                    (column.propertyName === "CarLogistId" && !this.props.groupByCarLogist),
            )
            .map(columnName);
        return {
            initialState: {
                ...(this.props.groupByCarLogist ? { groupBy: ["carLogistId"] } : {}),
                hiddenColumns,
            },
            renderRowSubComponent: (row, isPrintForm) => {
                if (!row.original?.bids || row.original?.bids.length === 0) {
                    return null;
                }
                const tableProps = isPrintForm
                    ? {
                          className: "p-3 reports--bg-bluelight",
                          style: {
                              minWidth: "100%",
                              width: row.cells.reduce((acc, cell) => acc + cell.column.width, 0),
                          },
                      }
                    : {
                          className: "table-responsive",
                      };
                return (
                    <div {...tableProps}>
                        <table className="table table-sm mb-0 border-bottom border-secondary bg-secondary">
                            <thead>
                                <tr className="table-active">
                                    <th>#</th>
                                    <th>Заявка</th>
                                    <th>Внешний ID Заявки</th>
                                    <th>Принятие заявки</th>
                                    <th>Отметка на загрузке</th>
                                    <th>Отметка на выгрузке</th>
                                    <th>Итог</th>
                                </tr>
                            </thead>
                            <tbody>
                                {row.original.bids.map((item, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td data-bidid={item.bidId}>
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={paths.bidShow("bid", item.bidId)}
                                            >
                                                {item.bidId}
                                            </a>
                                        </td>
                                        <td data-externalId={item.bidId}>{formatValue(item.externalId, "string")}</td>
                                        <td data-accepted={item.bidId}>{formatValue(item.accepted, "boolean")}</td>
                                        <td data-hasloaded={item.bidId}>{formatValue(item.hasLoaded, "boolean")}</td>
                                        <td data-hasunloaded={item.bidId}>
                                            {formatValue(item.hasUnloaded, "boolean")}
                                        </td>
                                        <td data-percent={item.bidId}>{formatValue(item.percent / 100, "percent")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            },
        };
    };
}

export default class ConfiguredReport extends Component {
    state = {
        groupByCarLogist: false,
    };

    toggleCheckbox = () => {
        this.setState({ groupByCarLogist: !this.state.groupByCarLogist });
    };

    render() {
        const checkboxCname = cn("checkbox d-inline-block mr-2", {
            "checkbox--checked": this.state.groupByCarLogist,
        });
        return (
            <>
                {!this.props.isPrintForm && (
                    <div>
                        <div className="mb-2 btn" onClick={this.toggleCheckbox}>
                            <span className={checkboxCname} />
                            <small>Группировать по логистам</small>
                        </div>
                    </div>
                )}
                <DriverAppUsageReport key={this.state.groupByCarLogist} {...this.props} {...this.state} />
            </>
        );
    }
}
